import React from 'react';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';
import {
  Crown, Loader2, CheckCircle2, XCircle, Pencil, Save, X,
  RefreshCw, ShieldCheck, Clock, Ban, Store
} from 'lucide-react';
import AvatarUpload from '../AvatarUpload';
import { PRO_PRICES } from '../../data/pricing';

const InfoField = ({ label, value, testId }) => (
  <div>
    <p className="font-sans text-[10px] font-bold uppercase tracking-widest text-stone-400 mb-1">{label}</p>
    <p className="font-sans text-sm text-stone-700" data-testid={testId}>{value}</p>
  </div>
);

const ProfileAccountTab = ({
  user, isPro, isEditing, editName, nameError, saving, saveStatus, saveMessage,
  subscription, loadingSub, subAction, showCancelDialog,
  startEditing, cancelEditing, setEditName, validateName, setNameError, handleSaveProfile,
  handleCancelSubscription, handleReactivateSubscription, setShowCancelDialog,
  updateUser, navigate, formatDate, formatCurrency,
}) => {
  const { isShopifyBilled, shopifyBilling } = useAuth();
  return (
    <div className="space-y-4" data-testid="account-section">
      {saveStatus && (
        <div data-testid="profile-save-status" className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-sans font-medium transition-all ${
          saveStatus === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {saveStatus === 'success' ? <CheckCircle2 className="w-4 h-4 flex-shrink-0" /> : <XCircle className="w-4 h-4 flex-shrink-0" />}
          {saveMessage}
        </div>
      )}

      {/* Avatar section */}
      <div className="bg-white rounded-2xl border border-stone-100 p-6">
        <h3 className="font-sans font-bold text-stone-800 text-sm mb-4">Profile picture</h3>
        <AvatarUpload user={user} onAvatarChange={(newUrl) => updateUser({ avatar_url: newUrl })} />
      </div>

      {/* Account details */}
      <div className="bg-white rounded-2xl border border-stone-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-sans font-bold text-stone-800 text-sm">Account details</h3>
          {!isEditing ? (
            <Button data-testid="edit-profile-btn" onClick={startEditing} variant="ghost" className="text-stone-500 hover:text-stone-700 h-8 px-3 text-xs font-sans">
              <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <Button data-testid="cancel-edit-btn" onClick={cancelEditing} variant="ghost" disabled={saving} className="text-stone-400 hover:text-stone-600 h-8 px-3 text-xs font-sans">
                <X className="w-3.5 h-3.5 mr-1" /> Cancel
              </Button>
              <Button data-testid="save-profile-btn" onClick={handleSaveProfile} disabled={saving} className="bg-[#B49A88] hover:bg-[#B49A88] text-white h-8 px-4 text-xs font-sans font-semibold rounded-full">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <p className="font-sans text-[10px] font-bold uppercase tracking-widest text-stone-400 mb-1">Email</p>
            <p className="font-sans text-sm text-stone-700" data-testid="account-email">{user?.email}</p>
            {isEditing && <p className="font-sans text-[10px] text-stone-400 mt-1">Email cannot be changed here</p>}
          </div>
          <div>
            <p className="font-sans text-[10px] font-bold uppercase tracking-widest text-stone-400 mb-1">Full name</p>
            {isEditing ? (
              <div>
                <input data-testid="edit-name-input" type="text" value={editName} onChange={(e) => { setEditName(e.target.value); if (nameError) setNameError(validateName(e.target.value)); }} onKeyDown={(e) => { if (e.key === 'Enter') handleSaveProfile(); }} maxLength={100} placeholder="Enter your full name" className={`w-full px-3 py-2 border rounded-xl font-sans text-sm text-stone-700 placeholder:text-stone-400 focus:outline-none transition-colors ${nameError ? 'border-red-300 focus:border-red-400' : 'border-stone-200 focus:border-[#B49A88]'}`} />
                {nameError && <p data-testid="name-error" className="font-sans text-xs text-red-500 mt-1">{nameError}</p>}
              </div>
            ) : (
              <p className="font-sans text-sm text-stone-700" data-testid="account-name">{user?.full_name || <span className="text-stone-400 italic">Not specified</span>}</p>
            )}
          </div>
          <InfoField label="Member since" value={user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'} testId="account-joined" />
          <InfoField label="User ID" value={user?.id?.slice(0, 8) + '...'} testId="account-id" />
        </div>
      </div>

      {/* Subscription Management */}
      <div className="bg-white rounded-2xl border border-stone-100 p-6" data-testid="subscription-section">
        <h3 className="font-sans font-bold text-stone-800 text-sm mb-5">Subscription</h3>

        {loadingSub ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#B49A88]" /></div>
        ) : !isPro ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-stone-100 flex items-center justify-center"><Crown className="w-5 h-5 text-stone-400" /></div>
              <div>
                <p className="font-sans font-bold text-stone-800 text-sm" data-testid="subscription-plan">Free plan</p>
                <p className="font-sans text-stone-400 text-xs">{isShopifyBilled ? `Connected via Shopify (${shopifyBilling.shopName || shopifyBilling.shopDomain || 'Store'})` : '3 generations/month'}</p>
              </div>
            </div>
            <Button data-testid="upgrade-btn" onClick={() => navigate('/pricing')} className="bg-[#B49A88] hover:bg-[#B49A88] text-white rounded-full px-5 h-9 text-xs font-sans font-semibold">
              {isShopifyBilled ? 'Upgrade via Shopify' : 'Upgrade to Pro'}
            </Button>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center"><Crown className="w-5 h-5 text-amber-500" /></div>
                <div>
                  <p className="font-sans font-bold text-stone-800 text-sm" data-testid="subscription-plan">Pro plan</p>
                  <p className="font-sans text-stone-400 text-xs">Unlimited generation</p>
                </div>
              </div>
              <div className={`px-3 py-1 rounded-full text-[10px] font-sans font-bold uppercase tracking-wider ${
                subscription?.cancel_at_period_end ? 'bg-amber-50 text-amber-600 border border-amber-200' : 'bg-emerald-50 text-emerald-600 border border-emerald-200'
              }`} data-testid="subscription-status-badge">
                {subscription?.cancel_at_period_end ? 'Cancelling' : 'Active'}
              </div>
            </div>

            {subscription?.cancel_at_period_end && subscription?.current_period_end && (
              <div className="flex items-start gap-3 bg-amber-50/70 border border-amber-100 rounded-xl p-4" data-testid="cancel-notice">
                <Clock className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-sans font-semibold text-amber-800 text-sm">Subscription cancelled</p>
                  <p className="font-sans text-amber-700 text-xs mt-0.5">
                    You keep Pro access until <strong>{formatDate(subscription.current_period_end)}</strong>. After that you return to the free plan.
                  </p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4 bg-stone-50 rounded-xl p-4">
              <div>
                <p className="font-sans text-[10px] font-bold uppercase tracking-widest text-stone-400 mb-1">Price</p>
                <p className="font-sans text-sm font-semibold text-stone-800" data-testid="subscription-price">
                  {subscription?.amount ? formatCurrency(subscription.amount, subscription.currency) : `$${PRO_PRICES.monthly.usd}`}
                  <span className="font-normal text-stone-400">/mo</span>
                </p>
              </div>
              <div>
                <p className="font-sans text-[10px] font-bold uppercase tracking-widest text-stone-400 mb-1">Payment method</p>
                <p className="font-sans text-sm text-stone-700" data-testid="subscription-provider">
                  {subscription?.provider === 'paypal' ? 'PayPal' : 'Stripe (Card)'}
                </p>
              </div>
              <div>
                <p className="font-sans text-[10px] font-bold uppercase tracking-widest text-stone-400 mb-1">
                  {subscription?.cancel_at_period_end ? 'Pro active until' : 'Next payment'}
                </p>
                <p className="font-sans text-sm text-stone-700" data-testid="subscription-next-date">
                  {subscription?.current_period_end ? formatDate(subscription.current_period_end) : '—'}
                </p>
              </div>
              <div>
                <p className="font-sans text-[10px] font-bold uppercase tracking-widest text-stone-400 mb-1">Auto-renewal</p>
                <p className="font-sans text-sm text-stone-700 flex items-center gap-1.5" data-testid="subscription-auto-renew">
                  {subscription?.cancel_at_period_end ? (
                    <><Ban className="w-3.5 h-3.5 text-amber-500" /> <span className="text-amber-600">Av</span></>
                  ) : (
                    <><RefreshCw className="w-3.5 h-3.5 text-emerald-500" /> <span className="text-emerald-600">On</span></>
                  )}
                </p>
              </div>
            </div>

            {subscription?.provider === 'stripe' && (
              <div className="pt-1">
                {subscription?.cancel_at_period_end ? (
                  <Button data-testid="reactivate-subscription-btn" onClick={handleReactivateSubscription} disabled={!!subAction}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-6 h-10 text-xs font-sans font-semibold">
                    {subAction === 'reactivating' ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-2" /> Reactivating...</> : <><RefreshCw className="w-3.5 h-3.5 mr-2" /> Reactivate subscription</>}
                  </Button>
                ) : (
                  <Button data-testid="cancel-subscription-btn" onClick={() => setShowCancelDialog(true)} disabled={!!subAction}
                    variant="outline" className="text-stone-500 hover:text-red-600 hover:border-red-200 rounded-full px-6 h-10 text-xs font-sans font-medium border-stone-200">
                    Cancel subscription
                  </Button>
                )}
              </div>
            )}

            {subscription?.provider === 'paypal' && (
              <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-xl p-3">
                <ShieldCheck className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <p className="font-sans text-xs text-blue-700 leading-relaxed">
                  You paid via PayPal (one-time purchase). The subscription does not renew automatically.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export { ProfileAccountTab };
